import {
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	DOCUMENT,
	ElementRef,
	inject,
	input,
	NgZone,
	output,
	signal,
} from '@angular/core';

import { CLARITY_FLY_IN_RESIZE, CLARITY_FLY_IN_RESIZE_TEXT } from '../constants/clarity-fly-in-resize.constant';

const PRIMARY_BUTTON = 0;

type ResizeDrag = {
	readonly pointerId: number;
	readonly startX: number;
	readonly startWidth: number;
};

/**
 * The separator on a fly-in's inline-start edge: a drag or the arrow keys widen the panel up to
 * {@link CLARITY_FLY_IN_RESIZE.MAX_VIEWPORT_SHARE} of the viewport, or to the width it opened at when
 * that is wider, and narrow it down to `minWidth`, or to the width it opened at when that is narrower.
 * It owns the gesture, never the width: every new width leaves through `resized`.
 */
@Component({
	selector: 'eff-clarity-fly-in-resize-handle',
	templateUrl: './clarity-fly-in-resize-handle.component.html',
	styleUrl: './clarity-fly-in-resize-handle.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		role: 'separator',
		tabindex: '0',
		'aria-orientation': 'vertical',
		'[attr.aria-label]': 'text.LABEL',
		'[attr.aria-valuemin]': 'floor()',
		'[attr.aria-valuemax]': 'valueMax()',
		'[attr.aria-valuenow]': 'valueNow()',
		'[attr.data-resizing]': 'isResizing()',
		'(pointerdown)': 'handlePointerDown($event)',
		'(pointerup)': 'handlePointerEnd()',
		'(pointercancel)': 'handlePointerEnd()',
		'(lostpointercapture)': 'handlePointerEnd()',
		'(keydown)': 'handleKeydown($event)',
	},
})
export class ClarityFlyInResizeHandleComponent {
	/** The element whose rendered width is the panel's. */
	public readonly panel = input.required<HTMLElement>();
	/** The narrowest width, in px, the reader may resize the panel to; lowered to the opening width when the panel opened narrower. */
	public readonly minWidth = input.required<number>();

	/** The width, in px, the panel should take. */
	public readonly resized = output<number>();

	private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
	private readonly zone = inject(NgZone);
	private readonly view = inject(DOCUMENT).defaultView;

	private readonly openingWidth = signal<number>(0);

	private drag: ResizeDrag | null = null;
	private pendingWidth: number | null = null;
	private pendingFrame: number | null = null;
	private stopListeningForMoves: (() => void) | null = null;

	protected readonly text = CLARITY_FLY_IN_RESIZE_TEXT;
	protected readonly valueNow = signal<number | null>(null);
	protected readonly valueMax = signal<number | null>(null);
	protected readonly isResizing = signal<boolean>(false);
	protected readonly floor = computed<number>(() => {
		const openingWidth = this.openingWidth();

		return openingWidth > 0 ? Math.min(this.minWidth(), openingWidth) : this.minWidth();
	});

	constructor() {
		afterNextRender((): void => {
			this.openingWidth.set(this.measure());
		});
		inject(DestroyRef).onDestroy((): void => {
			this.cancelPendingFrame();
			this.stopListeningForMoves?.();
		});
	}

	protected handlePointerDown(event: PointerEvent): void {
		if (event.button !== PRIMARY_BUTTON) {
			return;
		}

		event.preventDefault();

		this.drag = { pointerId: event.pointerId, startX: event.clientX, startWidth: this.measure() };
		this.host.nativeElement.setPointerCapture(event.pointerId);
		this.listenForMoves();
		this.isResizing.set(true);
	}

	protected handlePointerEnd(): void {
		if (this.drag === null) {
			return;
		}

		this.stopListeningForMoves?.();
		this.stopListeningForMoves = null;
		this.cancelPendingFrame();
		this.flushPendingResize();
		this.drag = null;
		this.isResizing.set(false);
	}

	protected handleKeydown(event: KeyboardEvent): void {
		const target = this.keyboardTarget(event.key);

		if (target === null) {
			return;
		}

		event.preventDefault();
		this.resizeTo(target);
	}

	private listenForMoves(): void {
		const element = this.host.nativeElement;
		const onMove = (event: PointerEvent): void => this.handlePointerMove(event);

		this.stopListeningForMoves?.();
		this.zone.runOutsideAngular((): void => element.addEventListener('pointermove', onMove));
		this.stopListeningForMoves = (): void => element.removeEventListener('pointermove', onMove);
	}

	private handlePointerMove(event: PointerEvent): void {
		const drag = this.drag;

		if (drag?.pointerId !== event.pointerId) {
			return;
		}

		this.scheduleResize(drag.startWidth + drag.startX - event.clientX);
	}

	private measure(): number {
		const width = Math.round(this.panel().getBoundingClientRect().width);

		this.valueNow.set(width);
		this.valueMax.set(this.maxWidth());

		return width;
	}

	private keyboardTarget(key: string): number | null {
		switch (key) {
			case 'ArrowLeft':
				return this.measure() + CLARITY_FLY_IN_RESIZE.KEYBOARD_STEP;
			case 'ArrowRight':
				return this.measure() - CLARITY_FLY_IN_RESIZE.KEYBOARD_STEP;
			case 'Home':
				return this.floor();
			case 'End':
				return this.maxWidth();
			default:
				return null;
		}
	}

	private maxWidth(): number {
		const viewportWidth = this.view?.innerWidth ?? 0;
		const viewportShare = Math.floor(viewportWidth * CLARITY_FLY_IN_RESIZE.MAX_VIEWPORT_SHARE);

		return Math.max(this.floor(), this.openingWidth(), viewportShare);
	}

	private scheduleResize(width: number): void {
		this.pendingWidth = width;

		if (this.pendingFrame !== null) {
			return;
		}

		this.pendingFrame = requestAnimationFrame((): void => {
			this.pendingFrame = null;
			this.zone.run((): void => this.flushPendingResize());
		});
	}

	private flushPendingResize(): void {
		const width = this.pendingWidth;
		this.pendingWidth = null;

		if (width !== null) {
			this.resizeTo(width);
		}
	}

	private cancelPendingFrame(): void {
		if (this.pendingFrame !== null) {
			cancelAnimationFrame(this.pendingFrame);
			this.pendingFrame = null;
		}
	}

	private resizeTo(width: number): void {
		const maxWidth = this.maxWidth();
		const clamped = Math.round(Math.min(Math.max(width, this.floor()), maxWidth));

		this.valueMax.set(maxWidth);
		this.valueNow.set(clamped);
		this.resized.emit(clamped);
	}
}
